// Entry point (esbuild bundles this → public/widget.js as a self-contained IIFE).
// Owns conversation state, drives the SSE stream, and decides the escalation path.

import { readConfig, type WidgetConfig } from "./config";
import { WidgetUI, type BotMessageHandle } from "./ui";
import {
  streamChat,
  postEscalate,
  postSignup,
  fetchHours,
  fetchBotEnabled,
  postHandoff,
  fetchPresence,
  openCustomerStream,
  postAck,
} from "./api";
import { addToCart } from "./cart";
import { reportRouted, rateChat, pingVisitor, sendCustomerTyping, uploadFile } from "./api";
import type { ChatMessage, EscalateEvent, ProductCard } from "./types";

/** Keep stored cards for only this many most-recent assistant messages — sessionStorage
 *  is ~5MB and unbounded growth across a long session is sloppy even if cards are small. */
const MAX_CARD_ENTRIES = 10;

declare global {
  interface Window {
    smartsupp?: (...args: any[]) => void;
    _smartsupp?: any;
    __dtsChatLoaded?: boolean;
  }
}

const STORE_KEY = "dts-chat-session";

// The conversation persists in localStorage (not sessionStorage) so a returning
// buyer continues the same thread across a tab close, a new tab, or a browser
// restart — B2B buying cycles routinely span a day (browse now, get sign-off,
// come back tomorrow). It resets after this much INACTIVITY: a sliding window
// refreshed on every persist(), so a stale thread can't linger past a day and
// bias the model. The header "Start new chat" button is the on-demand escape
// hatch (matters on shared facility PCs, where localStorage would otherwise show
// the previous person's chat until the window elapses).
const INACTIVITY_MS = 24 * 60 * 60 * 1000;

// Written into history as the assistant turn when a handoff produced no bot text.
// Without it the unanswered user message ("Track my order") persists in
// sessionStorage and biases the model into re-escalating every later question.
// Keep in sync with the escalation placeholder in src/eval/ab.ts.
const HANDOFF_HISTORY_NOTE = "I've connected you with our live support team for that.";

// A live agent's turns must land in history too. They are rendered as assistant
// messages, but the model only ever saw the customer's half of an agent-handled
// stretch, so a handback looked to it like its own handoff had failed. Tagged so
// the prompt (WHEN TO ESCALATE) can tell a human's words from its own.
const agentTurn = (name: string, body: string): string => `[Live agent ${name}]: ${body}`;
const agentJoinedNote = (name: string): string =>
  `[A live agent joined this chat: ${name}.]`;
const agentLeftNote = (name: string): string =>
  `[The live agent ${name} left and handed the chat back to me.]`;

// Written into history when the signup form renders (the model wrote zero text), so
// later turns know the form was offered instead of seeing a dangling user message.
const SIGNUP_HISTORY_NOTE =
  "Let's get your account set up. I've opened a short form here in the chat — fill it in and we'll email you a password setup link.";

/** Lead copy for the off-hours leave-a-message form: say plainly that CS is gone,
 *  when they're back (server-computed; generic fallback if unknown), and that this
 *  form IS the email — submissions land in the customer-service inbox. We used to
 *  also offer info@ "as an alternative", and ~1/3 of customers did BOTH, so CS got
 *  the same request twice (Bailey 8/3: own email 21:13Z, form 21:15Z; Abby 7/28).
 *  One channel only — do not reintroduce the "or email us" line (2026-08-04). */
function offHoursLead(nextOpen: string | null): string {
  const back = nextOpen ? `back ${nextOpen}` : "back during regular business hours";
  return (
    `Customer service isn't available right now — they're ${back}. ` +
    `Leave your details below and they'll follow up as soon as they're in. ` +
    `Your message goes straight to our customer service inbox, so there's no need ` +
    `to send a separate email.`
  );
}

interface PersistedState {
  sessionId: string;
  history: ChatMessage[];
  // How many history entries have already been imported into Smartsupp. Lets a repeat
  // handoff send only the new turns instead of re-feeding the whole transcript.
  handedOffUpTo: number;
  // Product cards from show_products turns, keyed by the index of the assistant history
  // entry they belong to. Optional so sessions persisted before this fix restore fine.
  cards?: Record<number, ProductCard[]>;
  // Enlarged-panel toggle (Task 6). Optional so sessions persisted before this fix
  // restore fine (defaults to collapsed).
  expanded?: boolean;
  // Contact details from any in-chat form (pre-handoff, escalate, signup), so a
  // returning customer is not asked twice. Optional for pre-2026-08-12 sessions.
  contact?: { name: string; email: string; orderRef?: string };
  // Epoch ms of the last activity (any persist). Drives the INACTIVITY_MS expiry
  // so a localStorage session past the window starts clean instead of resuming stale.
  lastActivity: number;
}

class ChatWidget {
  private config: WidgetConfig;
  private ui: WidgetUI;
  private sessionId: string;
  private history: ChatMessage[] = [];
  private streaming = false;
  private abort: AbortController | null = null;
  private lastUserMessage = "";
  // Count of history entries already imported into Smartsupp (high-water mark).
  // A repeat handoff in the same session sends only history.slice(handedOffUpTo).
  private handedOffUpTo = 0;
  // Product cards keyed by assistant history index; see PersistedState.cards.
  private cards: Record<number, ProductCard[]> = {};
  // Cards from the in-flight products event, stashed until the turn's assistant
  // message is pushed to history (so we know its final index).
  private pendingCards: ProductCard[] | null = null;
  /** One proactive greeting per page load, whatever the rules say. */
  private autoMessageShown = false;
  private lastTypingSent = 0;
  private lastPingedPage = '';
  // Enlarged-panel toggle; see PersistedState.expanded.
  private expanded = false;
  // ── Live-agent state (our own console, not Smartsupp) ──────────────────────
  /** Set between "connecting you to a person" and either an agent joining or
   *  the ring timing out. Guards the no_agent fallback so a stale event can't
   *  drop a lead form onto a customer who is already mid-conversation. */
  private awaitingAgent = false;
  /** Name of the agent currently on the thread, or null when the bot has it. */
  private liveAgent: string | null = null;
  private closeStream: (() => void) | null = null;
  /** Who this customer is, from any in-chat form. Gates live-agent handoffs. */
  private contact: { name: string; email: string; orderRef?: string } | null = null;
  /** Agent messages rendered while the panel was closed/hidden — acked as
   *  "read" the moment the customer actually looks. */
  private pendingReadIds: string[] = [];

  constructor() {
    this.config = readConfig();

    const restored = loadState();
    this.sessionId = restored ? restored.sessionId : "dts-" + genId();
    this.history = restored ? restored.history : [];
    this.handedOffUpTo = restored ? restored.handedOffUpTo : 0;
    this.cards = restored?.cards ?? {};
    this.expanded = restored?.expanded ?? false;
    this.contact = restored?.contact ?? null;

    const host = document.createElement("div");
    host.id = "dts-chat-widget";
    const shadow = host.attachShadow({ mode: "open" });
    host.style.setProperty("--dts-accent", this.config.accent);
    host.style.setProperty("--dts-accent-text", contrastColor(this.config.accent));
    document.body.appendChild(host);

    this.ui = new WidgetUI(shadow, this.config, {
      onSend: (text) => this.send(text),
      onSendFile: (file) => void this.sendFile(file),
      onTyping: () => this.notifyTyping(),
      onToggle: (open) => {
        if (open) this.flushReadAcks();
      },
      onRequestHuman: () => this.requestHuman(),
      onToggleExpand: () => this.toggleExpand(),
      onNewChat: () => this.newChat(),
      onAddToCart: (productId, variantId, quantity) => addToCart(productId, variantId, quantity),
    });

    // A message that arrived while the tab was hidden counts as read only once
    // the customer actually looks at it.
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") this.flushReadAcks();
    });

    this.ui.setQuickReplies(this.config.quickReplies);
    this.ui.setExpanded(this.expanded);
    this.renderInitial();
    this.connectLiveStream();
    this.startPresencePings();
  }

  /** Open the channel an agent's reply arrives on. Costs one idle connection
   *  per open widget; without it a CSR could type into the console and the
   *  customer would never see a word. */
  private connectLiveStream(): void {
    this.closeStream?.();
    this.closeStream = openCustomerStream(this.config.apiBase, this.sessionId, {
      onAgentMessage: (m) => this.receiveAgentMessage(m),
      onAgentJoined: (agentName) => this.onAgentJoined(agentName),
      onAgentLeft: (agentName, handedBackToBot) => this.onAgentLeft(agentName, handedBackToBot),
      onAgentTyping: () => this.ui.showTyping(),
      onNoAgent: () => this.onNoAgent(),
      onClosed: () => this.onAgentLeft(this.liveAgent ?? "Our team", false),
      onAutoMessage: (text) => this.receiveAutoMessage(text),
      onRatingRequest: () => this.ui.showRatingPrompt(async (rating, comment) => {
        await rateChat(this.config.apiBase, this.sessionId, rating, comment);
      }),
      onMessageDeleted: (id) => this.ui.removeMessage(id),
      onMessagesRead: () => this.onMessagesRead(),
    });
  }

  /**
   * A proactive greeting from a rule. Opens the panel if it is closed, because a
   * message nobody sees is worse than no message — but never steals focus from
   * whatever the customer is doing on the page.
   */
  /** Share a file with whoever is on the other end. */
  private async sendFile(file: File): Promise<void> {
    const result = await uploadFile(this.config.apiBase, this.sessionId, file);
    if (!result.ok) {
      this.ui.addSystemError(result.error);
      return;
    }
    this.ui.addAttachment(result.attachment, true);
    // Recorded in history as text so a handback to the bot, or a refresh, does
    // not lose the fact that a file was sent.
    this.history.push({ role: "user", content: `[sent a file: ${result.attachment.filename}]` });
    this.persist();
  }

  /** Throttled so a fast typist does not generate a request per keystroke. */
  private notifyTyping(): void {
    const now = Date.now();
    if (now - this.lastTypingSent < 3000) return;
    this.lastTypingSent = now;
    sendCustomerTyping(this.config.apiBase, this.sessionId);
  }

  /**
   * Keep the console's visitor list current. The storefront is a normal
   * multi-page site so a page change is usually a fresh load, but BigCommerce
   * navigates client-side in places — hence the interval as well as the
   * initial call.
   */
  private startPresencePings(): void {
    const ping = () => {
      try {
        if (location.href === this.lastPingedPage) return;
        this.lastPingedPage = location.href;
        pingVisitor(this.config.apiBase, this.sessionId, location.href, document.title);
      } catch {
        /* presence is a nicety */
      }
    };
    ping();
    setInterval(ping, 15000);
  }

  private receiveAutoMessage(text: string): void {
    if (!text || this.autoMessageShown) return;
    this.autoMessageShown = true;
    this.ui.addMessage("assistant", text);
    this.history.push({ role: "assistant", content: text });
    this.persist();
    this.ui.notifyUnread();
  }

  private receiveAgentMessage(m: {
    id?: string;
    body: string;
    agentName: string;
    attachment?: { filename: string; mimeType: string; sizeBytes: number; storageKey: string };
  }): void {
    if (!m.body) return;
    this.ui.hideTyping();
    this.awaitingAgent = false;
    this.liveAgent = m.agentName;
    this.ui.addMessage("assistant", m.body, m.id);
    this.history.push({ role: "assistant", content: agentTurn(m.agentName, m.body) });
    this.persist();
    // Read receipt: rendered = delivered; rendered while actually being looked
    // at = read. Hidden-tab arrivals are acked "read" on the next look.
    if (m.id) {
      const looking = this.ui.isOpen() && document.visibilityState === "visible";
      postAck(this.config.apiBase, {
        sessionId: this.sessionId,
        messageId: m.id,
        kind: looking ? "read" : "delivered",
      });
      if (!looking) this.pendingReadIds.push(m.id);
    }
    // A file sent by an agent needs to be clickable, not just described.
    if (m.attachment) this.ui.addAttachment(m.attachment, false);
    // Persist as an assistant turn so the thread survives a refresh and the bot
    // sees what the human already said if the chat is handed back.
    this.history.push({ role: "assistant", content: m.body });
    this.persist();
    this.ui.setInputEnabled(true);
  }

  /** The customer is now looking at the transcript — everything pending is read. */
  private flushReadAcks(): void {
    if (!this.pendingReadIds.length) return;
    const ids = this.pendingReadIds.splice(0);
    for (const id of ids) {
      postAck(this.config.apiBase, { sessionId: this.sessionId, messageId: id, kind: "read" });
    }
  }

  /** A CSR opened the thread — show "Seen" under the newest customer message.
   *  Only meaningful while a human owns (or is about to own) the chat. */
  private onMessagesRead(): void {
    if (this.liveAgent || this.awaitingAgent) this.ui.markLastCustomerSeen();
  }

  private onAgentJoined(agentName: string): void {
    this.awaitingAgent = false;
    this.liveAgent = agentName;
    this.ui.hideTyping();
    // Chat #1891 (2026-09-03): presence said nobody was free the instant she
    // clicked, so she got the leave-a-message form and closed the panel — then an
    // agent claimed the chat 53 seconds later. Her two messages were DELIVERED and
    // never READ, and she left without seeing that a person had arrived. A human
    // turning up is the one thing this customer explicitly asked for, so surface it.
    if (!this.ui.isOpen()) this.ui.setOpen(true);
    this.ui.addSystemNotice(`${agentName} has joined the chat.`);
    if (this.history[this.history.length - 1]?.role === "user") {
      this.history.push({ role: "assistant", content: agentJoinedNote(agentName) });
      this.persist();
    }
    this.ui.setInputEnabled(true);
    this.ui.focusInput();
  }

  private onAgentLeft(agentName: string, handedBackToBot: boolean): void {
    this.liveAgent = null;
    // A question the agent never got to. Chats #1867 and #1837 (2026-09-02) both
    // ended with the customer's last message unanswered by anyone: the agent handed
    // back while it sat there, and the bot only ever speaks in reply to a NEW
    // message, so both customers were left in silence — one of them for 26 minutes
    // until she gave up and went to checkout. Read it BEFORE the handback note goes
    // into history, and only when the trailing turn really is an unanswered
    // customer message (if the agent replied, the last turn is theirs).
    const trailing = this.history[this.history.length - 1];
    const unanswered =
      handedBackToBot && trailing?.role === "user" ? trailing.content : null;
    this.ui.addSystemNotice(
      handedBackToBot
        ? `${agentName} has left — I'll pick things back up from here.`
        : `${agentName} closed this chat. Send another message any time.`,
    );
    this.history.push({ role: "assistant", content: agentLeftNote(agentName) });
    this.persist();
    this.ui.setInputEnabled(true);
    if (unanswered) this.send(unanswered, { replayLast: true });
  }

  /** Nobody claimed the chat inside the ring window. Rather than leaving the
   *  customer staring at "connecting…", collect their details. */
  private onNoAgent(): void {
    if (!this.awaitingAgent) return;
    this.awaitingAgent = false;
    this.ui.hideTyping();
    // Nobody took it inside the ring window. Off-hours never reaches here (that
    // path shows its own form immediately), so this is always the in-hours
    // fallback ladder: Smartsupp while it is still installed, then our form.
    void this.routeAwayFromConsole({
      reason: "user_request",
      summary: this.lastUserMessage || "",
    });
  }

  private toggleExpand(): void {
    this.expanded = !this.expanded;
    this.ui.setExpanded(this.expanded);
    this.persist();
  }

  /** Header "Start new chat" — abandon the current thread and start clean: a new
   *  sessionId, empty history and cards, cleared storage, and back to the greeting.
   *  The on-demand reset for the persisted (localStorage) session — e.g. so the next
   *  person on a shared PC isn't handed the previous visitor's conversation. Panel
   *  size (expanded) is a UI preference, left untouched. */
  private newChat(): void {
    if (this.streaming && this.abort) {
      this.abort.abort(); // drop any in-flight answer — they're starting over
      this.finishTurn();
    }
    this.sessionId = "dts-" + genId();
    this.history = [];
    this.cards = {};
    this.pendingCards = null;
    this.handedOffUpTo = 0;
    this.lastUserMessage = "";
    this.awaitingAgent = false;
    this.liveAgent = null;
    clearState();
    this.ui.resetMessages();
    this.renderInitial();
    // New sessionId means a new stream key — the old channel would deliver
    // messages meant for the abandoned thread. The presence interval is already
    // running and reads this.sessionId each time, so it needs no restart.
    this.connectLiveStream();
    this.lastPingedPage = '';
  }

  private renderInitial(): void {
    if (this.history.length) {
      this.history.forEach((m, i) => {
        const msg = this.ui.addMessage(m.role, m.content);
        if (m.role === "assistant" && this.cards[i]) {
          this.ui.appendProductCards(msg, this.cards[i]);
        }
      });
      this.ui.hideQuickReplies();
    } else {
      if (this.config.greeting) this.ui.addMessage("assistant", this.config.greeting);
      this.ui.showQuickReplies();
    }
  }

  /** `replayLast` answers a customer message that is ALREADY in history and
   *  already on screen (the handback case) — no second bubble, no second history
   *  entry, and the request history stops before it so the model sees it as the
   *  turn it is answering rather than one it already answered. */
  private send(text: string, opts: { replayLast?: boolean } = {}): void {
    text = text.trim();
    if (!text || this.streaming) return;

    this.ui.hideQuickReplies();
    if (!opts.replayLast) this.ui.addMessage("user", text);
    this.lastUserMessage = text;

    // prior turns, excluding the message being answered. On a replay that message
    // is already in history with the handback note pushed in after it, so drop it
    // by value from the end rather than assuming it is last.
    const reqHistory = this.history.slice();
    if (opts.replayLast) {
      for (let i = reqHistory.length - 1; i >= 0; i--) {
        if (reqHistory[i].role === "user" && reqHistory[i].content === text) {
          reqHistory.splice(i, 1);
          break;
        }
      }
    }
    if (!opts.replayLast) this.history.push({ role: "user", content: text });
    this.persist();

    this.streaming = true;
    this.ui.setInputEnabled(false);
    this.ui.showTyping();

    let bot: BotMessageHandle | null = null;
    let botText = "";
    let pendingEscalate: EscalateEvent | null = null;
    let pendingSignup = false;

    const ensureBot = (): BotMessageHandle => {
      if (!bot) {
        this.ui.hideTyping();
        bot = this.ui.startBotStream();
      }
      return bot;
    };

    this.abort = new AbortController();
    streamChat(
      this.config.apiBase,
      { sessionId: this.sessionId, message: text, history: reqHistory, pageUrl: location.href },
      {
        onToken: (t) => {
          ensureBot().append(t);
          botText += t;
        },
        onSources: (sources) => {
          if (sources.length) ensureBot().setSources(sources);
        },
        onProducts: (products: ProductCard[]) => {
          if (products.length) {
            ensureBot().setProducts(products);
            this.pendingCards = products;
          }
        },
        onEscalate: (e) => {
          pendingEscalate = e;
        },
        onSignup: () => {
          pendingSignup = true;
        },
        onAgentOffer: (label) => {
          if (bot) bot.setAgentOffer(label);
        },
        onDone: (d) => {
          this.ui.hideTyping();
          if (bot) bot.finalize();
          if (botText) {
            this.history.push({ role: "assistant", content: botText });
            if (this.pendingCards) {
              this.cards[this.history.length - 1] = this.pendingCards;
              this.pruneCards();
            }
            this.persist();
          }
          this.pendingCards = null; // clear regardless — no assistant text means no history index to key it to
          if (pendingSignup) this.showSignupForm();
          else if (pendingEscalate) void this.handleEscalation(d.inHours, pendingEscalate, d.nextOpenText);
          else this.finishTurn();
        },
        onError: () => {
          this.ui.hideTyping();
          this.ui.addSystemNotice(
            "Sorry — my connection dropped for a second and I couldn't finish that. Please send it again.",
          );
          this.finishTurn();
        },
      },
      this.abort.signal,
    );
  }

  private finishTurn(): void {
    this.streaming = false;
    this.abort = null;
    this.ui.setInputEnabled(true);
    this.ui.focusInput();
  }

  /** Manual handoff — the header "Talk to a person" button. Works any time,
   *  independent of the model: ask the server whether we're in hours, then route. */
  private async requestHuman(): Promise<void> {
    if (this.streaming && this.abort) {
      this.abort.abort(); // drop any in-flight answer; the customer wants a human now
      this.finishTurn();
    }
    this.ui.hideQuickReplies();
    let inHours = false;
    let nextOpen: string | null = null;
    try {
      const hours = await fetchHours(this.config.apiBase);
      inHours = hours.inHours;
      nextOpen = hours.nextOpenText;
    } catch {
      inHours = false; // fail safe to leave-a-message
    }
    // The /handoff beacon now fires inside handleEscalation, per branch — the
    // agent-available path must collect contact details FIRST (John 2026-08-12:
    // never ring a CSR for a customer we cannot follow up with), while every
    // other manual path still beacons immediately so the monitoring log keeps
    // seeing handoffs that never touch /chat.
    await this.handleEscalation(inHours, { reason: "user_request", summary: "" }, nextOpen, true);
  }

  /** Record + ring: /handoff logs the handoff (manual only — bot escalations
   *  already logged via /chat), saves the contact if present, and rings the
   *  console. */
  private beaconHandoff(inHours: boolean, reason: string, manual: boolean): void {
    postHandoff(this.config.apiBase, {
      sessionId: this.sessionId,
      reason,
      inHours,
      lastUserMessage: this.lastUserMessage || undefined,
      pageUrl: location.href,
      contact: this.contact ?? undefined,
      source: manual ? "manual" : "bot",
    });
  }

  /** The "hold on, we're getting someone" state, shown once contact is on file. */
  private showConnecting(): void {
    this.awaitingAgent = true;
    this.ui.addSystemNotice("Connecting you with someone from our team — one moment.");
    this.ui.showTyping();
    // The wait is now the server's full ring window rather than a few seconds,
    // so say something partway through instead of leaving a typing dot going for
    // two minutes. Guarded on awaitingAgent: an agent who has already joined, or
    // a ring that has already expired, must not get a "still looking" line.
    window.setTimeout(() => {
      if (this.awaitingAgent) {
        this.ui.addSystemNotice("Still finding someone for you — thanks for holding.");
      }
    }, STILL_LOOKING_MS);
    // From here the SSE stream drives: agent_joined, or no_agent → lead form.
    this.finishTurn();
  }

  private async handleEscalation(
    inHours: boolean,
    esc: EscalateEvent,
    nextOpen: string | null = null,
    manual = false,
  ): Promise<void> {
    const reason = esc.reason || "user_request";
    // OFF-HOURS: keep the customer on our own form (John, 2026-07-27). Previously
    // both paths opened Smartsupp and off-hours visitors landed silently in
    // Smartsupp's offline form — nobody was told when a human would be back or that
    // info@ exists, and a Saturday refund request (order #132099) sat unanswered.
    // Our form states the reopen time, offers info@ as an alternative, and emails
    // the lead to ESCALATION_EMAIL.
    if (!inHours) {
      if (manual) this.beaconHandoff(inHours, reason, true);
      this.recordHandoffInHistory();
      reportRouted(this.config.apiBase, this.sessionId, "off_hours_form");
      this.showEmailCapture(esc, offHoursLead(nextOpen));
      this.finishTurn();
      return;
    }
    // IN HOURS, OUR OWN CONSOLE FIRST (2026-08-07), AND WE WAIT FOR IT
    // (John, 2026-09-03). Presence — is a CSR signed in and marked Available —
    // used to decide this outright, because the clock alone cannot know who is
    // at a desk (2026-08-06). But it is a single instant, and being wrong about
    // that instant is expensive: in chat #1891 it said nobody, the customer was
    // told "nobody's free to chat this second" eight seconds after asking for a
    // person, and an agent picked the chat up from the inbox fifty-three seconds
    // later — by then she had left her details and closed the panel. So in hours
    // every request now goes to the console and holds the full ring window; the
    // server's `no_agent` event, not a presence snapshot, is what gives up.
    this.recordHandoffInHistory();
    // Contact gate (John 2026-08-12): name + email (order # optional) before
    // a person is rung, so every live chat can be followed up. Skipped when
    // this session already gave details on any in-chat form. The /handoff
    // beacon is what rings the console, so nothing rings until this resolves —
    // the server holds a 90s fallback for abandoned forms.
    if (this.contact?.email) {
      this.beaconHandoff(inHours, reason, manual);
      this.showConnecting();
      return;
    }
    this.ui.addContactForm({
      lead:
        "Before I connect you with our team, please leave your name and email so we can follow up if we get disconnected.",
      onSubmit: async (data) => {
        this.contact = {
          name: data.name,
          email: data.email,
          orderRef: data.orderRef || undefined,
        };
        this.persist();
        this.beaconHandoff(inHours, reason, manual);
        this.showConnecting();
        return true;
      },
    });
    this.finishTurn();
  }

  /** The ring expired with nobody on our console. John's chain from 2026-08-21 —
   *  console, then Smartsupp, then email — picks up from here. Presence is read
   *  again rather than reused: two minutes have passed since the customer asked. */
  private async routeAwayFromConsole(esc: EscalateEvent): Promise<void> {
    const presence = await fetchPresence(this.config.apiBase);

    // During the Smartsupp parallel run we still try it; once the server flips
    // smartsuppFallback to false this collapses to the form.
    if (!presence.smartsuppFallback) {
      this.recordHandoffInHistory();
      reportRouted(this.config.apiBase, this.sessionId, "lead_form");
      this.showEmailCapture(
        esc,
        "I couldn't get anyone to pick up just now. Leave your details and our team will follow up shortly.",
      );
      this.finishTurn();
      return;
    }

    // Legacy path: hand off to Smartsupp. We fall back to our own email-capture
    // form if Smartsupp isn't on the page, or if the "open" call only reached a
    // pre-init queue stub that never actually loads (ad-blockers silently block
    // loader.js, and the stub still answers calls like a function — see
    // verifySmartsuppReady).
    const attempted = this.openSmartsupp();
    if (attempted) {
      this.ui.showTyping(); // cover the verification wait; hidden either way below
      const ready = await this.verifySmartsuppReady();
      this.ui.hideTyping();
      if (ready) {
        this.sendHandoffContext(); // give the live agent the bot conversation (before the history note, so the import stays clean)
        this.recordHandoffInHistory();
        this.handedOffUpTo = this.history.length; // mark everything sent so a repeat handoff only imports new turns
        this.persist();
        reportRouted(this.config.apiBase, this.sessionId, "smartsupp");
        this.ui.hideWidget(); // close our chat + hide our bubble; Smartsupp is the surface now
        this.finishTurn();
        return;
      }
      // Smartsupp's queue stub answered but real init never landed within the timeout —
      // don't hide our widget over a dead handoff (that stranded three in-hours buyers).
      this.recordHandoffInHistory();
      reportRouted(this.config.apiBase, this.sessionId, "lead_form");
      this.showEmailCapture(
        esc,
        "I couldn't reach live chat just now. Leave your details below and our team will follow up.",
      );
      this.finishTurn();
      return;
    }
    // Smartsupp unavailable on this page — fall back to our own leave-a-message form.
    this.recordHandoffInHistory();
    reportRouted(this.config.apiBase, this.sessionId, "lead_form");
    this.showEmailCapture(
      esc,
      "I couldn't reach live chat just now. Leave your details below and our team will follow up.",
    );
    this.finishTurn();
  }

  /** Poll for proof that Smartsupp really initialized, not just that its pre-init queue
   *  stub answered a call — ad-blockers stub `window.smartsupp` even when loader.js is
   *  blocked, so `openSmartsupp()` returning true isn't proof a live chat exists. Real
   *  init shows up as DOM Smartsupp injects into the main document (our own UI lives in
   *  a shadow root under a different host element, so these selectors can't
   *  false-positive on it) or the stub's queue array (`smartsupp._`) being gone once the
   *  real snippet replaces it. Either signal is sufficient. Resolves false on timeout. */
  private verifySmartsuppReady(timeoutMs = 8000): Promise<boolean> {
    const isReady = (): boolean => {
      if (
        document.querySelector('iframe[src*="smartsupp"], #smartsupp-widget-container, #chat-application')
      ) {
        return true;
      }
      const raw = window.smartsupp as any;
      return typeof raw === "function" && raw._ === undefined;
    };

    return new Promise((resolve) => {
      if (isReady()) {
        resolve(true);
        return;
      }
      const start = Date.now();
      const interval = setInterval(() => {
        if (isReady()) {
          clearInterval(interval);
          resolve(true);
        } else if (Date.now() - start >= timeoutMs) {
          clearInterval(interval);
          resolve(false);
        }
      }, 300);
    });
  }

  /** Close the turn in persisted history when a handoff left the last user message
   *  unanswered (the model writes zero text on escalation). Only fires when there IS
   *  a dangling user turn, so history never starts with an assistant message. */
  private recordHandoffInHistory(): void {
    const last = this.history[this.history.length - 1];
    if (last && last.role === "user") {
      this.history.push({ role: "assistant", content: HANDOFF_HISTORY_NOTE });
      this.persist();
    }
  }

  /** Render the in-chat account signup form (offer_account_signup tool). The intro
   *  note goes into history so later turns know the form was offered. */
  private showSignupForm(): void {
    const last = this.history[this.history.length - 1];
    if (last && last.role === "user") {
      this.history.push({ role: "assistant", content: SIGNUP_HISTORY_NOTE });
      this.persist();
    }
    this.ui.addSignupForm({
      lead: SIGNUP_HISTORY_NOTE,
      onSubmit: (data) => {
        this.contact = {
          name: `${data.firstName} ${data.lastName}`.trim(),
          email: data.email,
          orderRef: this.contact?.orderRef,
        };
        this.persist();
        return postSignup(this.config.apiBase, {
          sessionId: this.sessionId,
          firstName: data.firstName,
          lastName: data.lastName,
          email: data.email,
          company: data.company || undefined,
          phone: data.phone || undefined,
        });
      },
    });
    this.finishTurn();
  }

  private showEmailCapture(esc: EscalateEvent, leadOverride?: string): void {
    const lead =
      leadOverride ||
      "I need to get a live Customer Service Agent to help with that. Leave your details below and our team will follow up during office hours.";
    this.ui.addEscalateForm({
      lead,
      defaultMessage: esc.summary || this.lastUserMessage || "",
      onSubmit: (data) => {
        // Any validated form doubles as the session's contact details, so a
        // later "talk to a person" doesn't ask again.
        this.contact = { name: data.name || "", email: data.email, orderRef: this.contact?.orderRef };
        this.persist();
        return postEscalate(this.config.apiBase, {
          sessionId: this.sessionId,
          email: data.email,
          name: data.name || undefined,
          message: data.message,
          transcript: this.history.slice(),
        });
      },
    });
  }

  /** Attempt to open the existing Smartsupp live chat. Lazy-loads it if a key is
   *  configured. We chat:show before chat:open so a launcher we hid at boot reappears
   *  and the chat window can display (chat:show is a no-op when it was never hidden).
   *  A `true` return only means a `window.smartsupp` call was made without throwing —
   *  it can be the pre-init queue stub, not a real chat. Callers must confirm with
   *  verifySmartsuppReady() before treating the handoff as successful. */
  private openSmartsupp(): boolean {
    setSmartsuppHardHidden(false); // undo the CSS hard-hide so the live chat can actually render
    if (typeof window.smartsupp === "function") {
      try {
        window.smartsupp("chat:show");
        window.smartsupp("chat:open");
        return true;
      } catch {
        /* fall through */
      }
    }
    if (this.config.smartsuppKey) {
      loadSmartsupp(this.config.smartsuppKey);
      try {
        window.smartsupp!("chat:show");
        window.smartsupp!("chat:open");
        return true;
      } catch {
        /* fall through */
      }
    }
    return false;
  }

  /** Restore our widget to its resting state — called when the visitor closes the
   *  live chat (Smartsupp messenger_close) so the bot becomes available again. */
  show(): void {
    this.ui.showWidget();
  }

  /** Push the bot conversation into Smartsupp so the live agent has context. Sent as
   *  the visitor's opening message(s); Smartsupp queues them until the chat is ready.
   *  Multi-part sends are staggered so the parts arrive in order. On a repeat handoff
   *  only the turns since the last handoff are sent, so the agent isn't re-fed the whole
   *  transcript each time the visitor bounces between the bot and live chat. */
  private sendHandoffContext(): void {
    try {
      if (typeof window.smartsupp !== "function") return;
      const isContinuation = this.handedOffUpTo > 0;
      const delta = this.history.slice(this.handedOffUpTo);
      const parts = buildHandoffMessages(delta, isContinuation);
      parts.forEach((part, i) => {
        setTimeout(() => {
          try {
            window.smartsupp!("chat:send", part);
          } catch {
            /* ignore — context is best-effort */
          }
        }, i * 400);
      });
    } catch {
      /* ignore — context is best-effort; the agent can still ask */
    }
  }

  /** Keep card entries only for the last MAX_CARD_ENTRIES assistant messages —
   *  unbounded growth across a long session is sloppy even though cards are small. */
  private pruneCards(): void {
    const assistantIndexes: number[] = [];
    this.history.forEach((m, i) => {
      if (m.role === "assistant") assistantIndexes.push(i);
    });
    const keep = new Set(assistantIndexes.slice(-MAX_CARD_ENTRIES));
    for (const key of Object.keys(this.cards)) {
      if (!keep.has(Number(key))) delete this.cards[Number(key)];
    }
  }

  private persist(): void {
    saveState({
      sessionId: this.sessionId,
      history: this.history,
      handedOffUpTo: this.handedOffUpTo,
      cards: this.cards,
      expanded: this.expanded,
      contact: this.contact ?? undefined,
      lastActivity: Date.now(), // refreshes the sliding INACTIVITY_MS window
    });
  }
}

// Unicode bold labels: Smartsupp has no documented rich-text support in visitor
// messages, so markdown like **bold** would show as literal asterisks. These glyphs
// render bold in any browser font, in both the visitor window and the agent dashboard.
const LABEL_CUSTOMER = "🧑 𝗖𝘂𝘀𝘁𝗼𝗺𝗲𝗿:";
const LABEL_CHATBOT = "🤖 𝗖𝗵𝗮𝘁𝗯𝗼𝘁:";

/** How long into the console ring window to tell the customer we are still
 *  looking. The ring itself is AGENT_RING_SECONDS on the server (120s today);
 *  this only has to land comfortably inside it. */
const STILL_LOOKING_MS = 45_000;

/** Each chatbot reply is trimmed to this many characters in the handoff transcript.
 *  Agents need the customer's exact words; the bot's product lists are context noise. */
const BOT_MSG_MAX = 280;
/** Per-message size Smartsupp accepts comfortably; long chats split into parts. */
const PART_MAX = 1400;

/** The bot conversation as Smartsupp handoff message(s): an intro line, then every
 *  turn ("Customer:" verbatim, "Chatbot:" trimmed) with blank lines between, split at
 *  turn boundaries into parts when the chat is long. On a continuation (the visitor
 *  already handed off once this session) `history` holds only the new turns and the
 *  wording says they're back, rather than re-introducing the whole chat. */
function buildHandoffMessages(history: ChatMessage[], isContinuation = false): string[] {
  const intro = isContinuation
    ? "I'm back and would like to talk to a person again."
    : "Hi, I'd like to talk to a person.";
  const turns = history.map((m) => {
    if (m.role === "user") return LABEL_CUSTOMER + " " + m.content;
    const text = m.content.length > BOT_MSG_MAX ? m.content.slice(0, BOT_MSG_MAX).trimEnd() + " …" : m.content;
    return LABEL_CHATBOT + " " + text;
  });
  if (turns.length === 0) return [intro];

  const chunks: string[] = [];
  let current = "";
  for (const turn of turns) {
    // A single oversized turn (huge paste) gets hard-split rather than dropped.
    const pieces = turn.length > PART_MAX ? (turn.match(new RegExp(`[\\s\\S]{1,${PART_MAX}}`, "g")) ?? []) : [turn];
    for (const piece of pieces) {
      if (current && current.length + piece.length + 2 > PART_MAX) {
        chunks.push(current);
        current = piece;
      } else {
        current = current ? current + "\n\n" + piece : piece;
      }
    }
  }
  if (current) chunks.push(current);

  const head = isContinuation
    ? intro + "\n\nHere's what I did with the website assistant since we last talked:"
    : intro + "\n\nChat with the website assistant so far:";
  if (chunks.length === 1) return [head + "\n\n" + chunks[0]];
  return [head, ...chunks.map((c, i) => `(part ${i + 1}/${chunks.length})\n\n${c}`)];
}

// ── Helpers ──────────────────────────────────────────

function loadState(): PersistedState | null {
  try {
    const raw = localStorage.getItem(STORE_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw);
    if (p && typeof p.sessionId === "string" && Array.isArray(p.history)) {
      // Expire after INACTIVITY_MS of no activity — a visitor returning past the
      // window starts a clean session rather than resuming a day-old thread.
      // A missing timestamp (foreign/corrupt blob) counts as expired.
      const lastActivity = typeof p.lastActivity === "number" ? p.lastActivity : 0;
      if (Date.now() - lastActivity > INACTIVITY_MS) {
        clearState();
        return null;
      }
      return {
        sessionId: p.sessionId,
        history: p.history,
        // Older sessions (pre-delta-handoff) have no mark — default to 0 so the next
        // handoff sends full context once, rather than nothing.
        handedOffUpTo: typeof p.handedOffUpTo === "number" ? p.handedOffUpTo : 0,
        // Older sessions (pre-card-persist) have no cards — default to {} so restore is safe.
        cards: p.cards && typeof p.cards === "object" ? p.cards : {},
        // Older sessions (pre-resize) have no expanded flag — default to collapsed.
        expanded: typeof p.expanded === "boolean" ? p.expanded : false,
        contact:
          p.contact && typeof p.contact.email === "string" && typeof p.contact.name === "string"
            ? {
                name: p.contact.name,
                email: p.contact.email,
                orderRef: typeof p.contact.orderRef === "string" ? p.contact.orderRef : undefined,
              }
            : undefined,
        lastActivity,
      };
    }
  } catch {
    /* localStorage blocked or corrupt — start fresh */
  }
  return null;
}

function saveState(state: PersistedState): void {
  try {
    localStorage.setItem(STORE_KEY, JSON.stringify(state));
  } catch {
    /* ignore quota/privacy errors */
  }
}

/** Wipe the persisted conversation (localStorage). Used by "Start new chat" and by
 *  loadState when the inactivity window has elapsed. */
function clearState(): void {
  try {
    localStorage.removeItem(STORE_KEY);
  } catch {
    /* ignore */
  }
}

function genId(): string {
  try {
    if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  } catch {
    /* fall through to manual */
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/** Pick black or white text for readability against the given hex accent. */
function contrastColor(color: string): string {
  const hex = color.trim().replace(/^#/, "");
  let r: number, g: number, b: number;
  if (hex.length === 3) {
    r = parseInt(hex[0] + hex[0], 16);
    g = parseInt(hex[1] + hex[1], 16);
    b = parseInt(hex[2] + hex[2], 16);
  } else if (hex.length === 6) {
    r = parseInt(hex.slice(0, 2), 16);
    g = parseInt(hex.slice(2, 4), 16);
    b = parseInt(hex.slice(4, 6), 16);
  } else {
    return "#ffffff"; // non-hex color — assume dark accent
  }
  if (isNaN(r) || isNaN(g) || isNaN(b)) return "#ffffff";
  const luminance = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
  return luminance > 0.6 ? "#11202a" : "#ffffff";
}

/** Standard Smartsupp loader snippet (queues calls until loader.js arrives). */
function loadSmartsupp(key: string): void {
  if (window.smartsupp) return;
  window._smartsupp = window._smartsupp || {};
  window._smartsupp.key = key;
  const w = window as any;
  w.smartsupp = function () {
    (w.smartsupp._ = w.smartsupp._ || []).push(arguments);
  };
  const first = document.getElementsByTagName("script")[0];
  const s = document.createElement("script");
  s.type = "text/javascript";
  s.async = true;
  s.charset = "utf-8";
  s.src = "https://www.smartsuppchat.com/loader.js?";
  if (first && first.parentNode) first.parentNode.insertBefore(s, first);
  else document.head.appendChild(s);
}

/** Id of the global CSS rule that force-hides Smartsupp. Kept as a plain <style> in the
 *  MAIN document head (Smartsupp renders into <body>, not our shadow root). */
const SMARTSUPP_HIDE_STYLE_ID = "dts-smartsupp-hard-hide";

/** Inject or remove a `display:none !important` rule covering every node Smartsupp adds
 *  to the page — its launcher iframe (`#chat-application-iframe`) and wrapper
 *  (`#smartsupp-widget-container`). `!important` beats any inline style Smartsupp sets,
 *  so its OFFLINE launcher — which an offline auto-message/trigger force-shows,
 *  overriding `hideWidget`/`chat:hide` — stays hidden after hours too. Idempotent. */
function setSmartsuppHardHidden(hidden: boolean): void {
  const existing = document.getElementById(SMARTSUPP_HIDE_STYLE_ID);
  if (hidden) {
    if (existing) return;
    const style = document.createElement("style");
    style.id = SMARTSUPP_HIDE_STYLE_ID;
    style.textContent =
      '#smartsupp-widget-container,[id^="smartsupp-"],iframe[id^="chat-application"],div[id^="chat-application"],iframe[src*="smartsupp"]{display:none !important;}';
    (document.head || document.documentElement).appendChild(style);
  } else if (existing) {
    existing.remove();
  }
}

/** Hide Smartsupp's own floating launcher so only our bubble shows. Smartsupp stays
 *  fully loaded — the handoff reopens it (chat:show + chat:open in openSmartsupp).
 *  Uses both the Smartsupp JS API (chat:hide + hideWidget) AND a CSS hard-hide: the JS
 *  calls are honoured in-hours, but after hours Smartsupp force-shows its launcher via
 *  an offline trigger that ignores hideWidget, so the CSS rule is what actually keeps
 *  the two launchers from overlapping. */
function hideSmartsuppLauncher(): void {
  setSmartsuppHardHidden(true);
  try {
    window._smartsupp = window._smartsupp || {};
    window._smartsupp.hideWidget = true; // honoured if Smartsupp (re)reads its config
  } catch {
    /* ignore */
  }
  try {
    window.smartsupp?.("chat:hide");
  } catch {
    /* ignore */
  }
}

/** Make sure Smartsupp's launcher is visible (undo any earlier hide) when the bot is
 *  OFF, so the storefront cleanly falls back to Smartsupp's own chat. */
function showSmartsuppLauncher(): void {
  setSmartsuppHardHidden(false);
  try {
    window.smartsupp?.("chat:show");
  } catch {
    /* ignore */
  }
}

/** Send the Google Customer Reviews badge behind any open chat window. The badge pins
 *  itself bottom-right at the maximum z-index (2147483647) and re-applies its own
 *  position, so it otherwise renders on top of the open chat window and overlaps the
 *  composer. It's injected dynamically (no editable snippet) and we have no Script
 *  Manager access, so we override it here with a global <style> in the page head — not
 *  the shadow root, since the badge lives in the main document. Its z-index is a plain
 *  inline style, so an !important rule wins; 2000 keeps it above page content but below
 *  any chat window. Runs whether the bot is ON or OFF, because Smartsupp collides too. */
function fixGoogleBadgeOverlap(): void {
  const id = "dts-google-badge-fix";
  if (document.getElementById(id)) return;
  const style = document.createElement("style");
  style.id = id;
  style.textContent = "#google-merchantwidget-iframe-wrapper{z-index:2000 !important;}";
  (document.head || document.documentElement).appendChild(style);
}

// ── Boot (guard against double-injection) ────────────
// Read the storefront master switch first. ON → show our bot and hide Smartsupp's
// launcher. OFF → stay out of the way and make sure Smartsupp's launcher shows. Fails
// closed to OFF (Smartsupp stays) if /config can't be reached.
async function boot(): Promise<void> {
  if (window.__dtsChatLoaded) return;
  window.__dtsChatLoaded = true;

  fixGoogleBadgeOverlap(); // independent of the ON/OFF switch — Smartsupp collides with the badge too

  const enabled = await fetchBotEnabled(readConfig().apiBase);
  if (!enabled) {
    showSmartsuppLauncher();
    return;
  }
  hideSmartsuppLauncher();
  const widget = new ChatWidget();

  // When the visitor closes the live chat, return to bot mode: re-hide Smartsupp's
  // launcher and bring our bubble back, so the two launchers never overlap.
  try {
    window.smartsupp?.("on", "messenger_close", () => {
      hideSmartsuppLauncher();
      widget.show();
    });
  } catch {
    /* ignore — bot still works; only the auto-return is unavailable */
  }
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", boot);
} else {
  boot();
}
